import { useEffect } from 'react';
import urlTemplate from 'url-template';
import type {
  CapacityManagementRootResponse,
  NwpCapacityGraphResponse,
  NwpCapacityGraphParams,
} from 'src/apis/capacity-management/types';
import type { CapacityManagementRootRequest } from 'src/apis/capacity-management/use-capacity-management-root';
import { useAxios } from 'src/hooks/use-axios';
import { Forbidden } from 'src/utils/errors';
import { isConstraintViolation } from 'src/utils/is-constraint-violation';

type HookConfig = {
  capacityManagementRoot: CapacityManagementRootRequest;
  params: NwpCapacityGraphParams;
};

type RequestOptions = {
  rootData: CapacityManagementRootResponse;
  params: NwpCapacityGraphParams;
};

export function useNwpCapacityGraph({
  capacityManagementRoot,
  params,
}: HookConfig) {
  const capacityGraph = useAxios(
    (axios, baseConfig, { rootData, params }: RequestOptions) => {
      if (!rootData._links?.capacityGraph) throw new Forbidden();

      const url = urlTemplate
        .parse(rootData._links.capacityGraph.href)
        .expand(params);

      return axios.request<NwpCapacityGraphResponse>({
        ...baseConfig,
        // params,
        url,
      });
    },
    {
      neededOnPageLoad: true,
      onError(error) {
        if (isConstraintViolation(error) && capacityGraph.response) {
          return error;
        } else {
          throw error;
        }
      },
    }
  );

  useEffect(() => {
    if (!capacityManagementRoot.response) return;

    const rootData = capacityManagementRoot.response.data;
    const options = { rootData, params };

    capacityGraph.execute(options);
    // oxlint-disable-next-line exhaustive-deps
  }, [capacityManagementRoot.response, params]);

  return capacityGraph;
}

export type NwpCapacityGraphRequest = ReturnType<typeof useNwpCapacityGraph>;
